import React from 'react';
import { useTranslation } from 'react-i18next';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import PercentageBarCell from '@lib/components/Table/commonComponents/PercentageBarCell/PercentageBarCell';
import Typography from '@lib/components/Typography/Typography';
import { PlanState } from '@lib/enums/plan';
import {
  calculateActiveUsersPercentage,
  getPlanState,
} from '@lib/utils/accessesManagement';
import formatCount from '@lib/utils/formatCount';
import entityViewStyles from '@lib/assets/styles/EntityView.module.scss';

export interface PlanUsersSectionProps {
  activeUsersCount?: number | null;
  usersLimit?: number | null;
}

/**
 * Seats used against the plan's limit: the count (badged once it runs close to
 * or into the limit) and the same ratio as a bar.
 */
export default function PlanUsersSection({
  activeUsersCount,
  usersLimit,
}: PlanUsersSectionProps) {
  const { t, i18n } = useTranslation();
  const usersCount = activeUsersCount ?? 0;
  const activeUsersPercentage = calculateActiveUsersPercentage(
    usersCount,
    usersLimit,
  );
  const planState = getPlanState(activeUsersPercentage);

  return (
    <>
      <div className={entityViewStyles.infoItem}>
        <p className={entityViewStyles.infoItemLabel}>
          <MaterialIcon icon="group" symbolsOutlined />
          <Typography variant="caption">{t('users')}</Typography>
        </p>
        <div className={entityViewStyles.infoItemContentBox}>
          <Typography variant="body">
            {formatCount(usersCount, i18n.language)}
            {usersLimit ? ` / ${formatCount(usersLimit, i18n.language)}` : ''}
          </Typography>
          {planState === PlanState.Warning && (
            <Chip
              type={ChipTypes.chipOrange}
              leftIcon="warning"
              size={ChipSizes.small}
              tooltip={t('user-limit-almost-reached')}
            />
          )}
          {planState === PlanState.Attention && (
            <Chip
              type={ChipTypes.chipRed}
              leftIcon="error"
              size={ChipSizes.small}
              tooltip={t('user-limit-reached')}
            />
          )}
        </div>
      </div>
      <div className={entityViewStyles.infoItem}>
        <p className={entityViewStyles.infoItemLabel}>
          <MaterialIcon icon="group_add" symbolsOutlined />
          <Typography variant="caption">{t('users-limit')}</Typography>
        </p>
        <div className={entityViewStyles.infoItemContent}>
          {usersLimit ? (
            <PercentageBarCell percentage={activeUsersPercentage} />
          ) : (
            <Typography variant="body">-</Typography>
          )}
        </div>
      </div>
    </>
  );
}
